import { createFilteredUrl } from "@/hooks/useUrlState";
import { TODAY } from "@/lib/utils";
import { startDateForBillables } from "@/modules/reports/billables/status/constant";
import { AmountCell2 } from "@/modules/work/billables/cells";
import { EmployeeCell } from "components/cell/employee/cell";
import CustomList from "components/list/custom";
import { DisplayAmount } from "components/display/amount";
import { Link } from "react-router-dom";
import { formatDateForAPI } from "utils/utils";
import { subDays } from "date-fns";
import { FIRST_DUE_DATE } from "@/lib/constants";

// Helper function to create company-based filter structure
const createCompanyFilters = (salesCoordinatorId, status, dateFilters = {}) => {
  const billableFilters = {
    status: { _eq: status },
    ...dateFilters,
  };

  return {
    contacts: {
      projects: {
        sales_coordinator: { _eq: salesCoordinatorId },
        jobs: {
          billables: billableFilters,
        },
      },
    },
    deep: {
      contacts: {
        _filter: {
          projects: {
            sales_coordinator: { _eq: salesCoordinatorId },
            jobs: {
              billables: billableFilters,
            },
          },
        },
        projects: {
          _filter: {
            sales_coordinator: { _eq: salesCoordinatorId },
            jobs: {
              billables: billableFilters,
            },
          },
          jobs: {
            _filter: {
              billables: billableFilters,
            },
            billables: {
              _filter: billableFilters,
            },
          },
        },
      },
    },
  };
};

export function Scbillables() {
  const columns = [
    {
      accessorKey: "sales_coordinator",
      header: "SC",
      cell: EmployeeCell,
    },
    {
      accessorKey: "project_count",
      header: "No. of Projects",
      cell: ({ getValue, row }) => {
        const salesCoordinatorId = row.original.sales_coordinator?.id;

        const filters = {
          sales_coordinator: { _eq: salesCoordinatorId },
          status: { _eq: "active" },
          jobs: {
            billables: {
              status: { _in: ["contracted", "pending_invoice", "billed"] },
            },
          },
        };

        return (
          <Link
            to={createFilteredUrl("/work/projects", filters)}
            className="hover:underline"
            target="_blank"
            rel="noopener noreferrer"
          >
            {getValue()}
          </Link>
        );
      },
      footer: (totals) => {
        return totals.project_count;
      },
    },
    {
      accessorKey: "contractedAmount",
      header: "Contracted",
      cell: ({ getValue, row }) => {
        const salesCoordinatorId = row.original.sales_coordinator?.id;
        const filters = createCompanyFilters(salesCoordinatorId, "contracted");

        return (
          <Link
            className="hover:underline"
            to={createFilteredUrl("../status/contracted", filters)}
            target="_blank"
            rel="noopener noreferrer"
          >
            <AmountCell2 getValue={getValue} />
          </Link>
        );
      },
      footer: (totals) => {
        return <AmountCell2 getValue={() => totals.contractedAmount} />;
      },
    },
    {
      accessorKey: "delayed",
      header: "Delayed",
      cell: ({ getValue, row }) => {
        const salesCoordinatorId = row.original.sales_coordinator?.id;
        const filters = createCompanyFilters(salesCoordinatorId, "contracted", {
          billing_date: {
            _lte: formatDateForAPI(new Date()),
          },
        });

        return (
          <Link
            className="hover:underline"
            to={createFilteredUrl("../status/contracted", filters)}
            target="_blank"
            rel="noopener noreferrer"
          >
            <AmountCell2 getValue={getValue} />
          </Link>
        );
      },
      footer: (totals) => {
        return <DisplayAmount value={totals.delayed} />;
      },
    },
    {
      accessorKey: "due",
      header: "Overdue",
      cell: ({ getValue, row }) => {
        const salesCoordinatorId = row.original.sales_coordinator?.id;
        const filters = createCompanyFilters(salesCoordinatorId, "billed", {
          due_date: {
            _lte: formatDateForAPI(new Date()),
            _gte: formatDateForAPI(FIRST_DUE_DATE),
          },
        });

        return (
          <Link
            className="hover:underline"
            to={createFilteredUrl("../status/billed", filters)}
            target="_blank"
            rel="noopener noreferrer"
          >
            <AmountCell2 getValue={getValue} />
          </Link>
        );
      },
      footer: (totals) => {
        return <DisplayAmount value={totals.due} />;
      },
    },
    {
      accessorKey: "zeroToFifteen",
      header: "0-15 Days",
      cell: ({ getValue, row }) => {
        const salesCoordinatorId = row.original.sales_coordinator?.id;
        const filters = createCompanyFilters(salesCoordinatorId, "billed", {
          due_date: {
            _gte: formatDateForAPI(subDays(new Date(), 15)),
            _lte: formatDateForAPI(new Date()),
          },
        });

        return (
          <Link
            className="hover:underline"
            to={createFilteredUrl("../status/billed", filters)}
            target="_blank"
            rel="noopener noreferrer"
          >
            <DisplayAmount value={getValue()} />
          </Link>
        );
      },
      footer: (totals) => {
        return <DisplayAmount value={totals.zeroToFifteen} />;
      },
    },
    {
      accessorKey: "sixteenToThirty",
      header: "16-30 Days",
      cell: ({ getValue, row }) => {
        const salesCoordinatorId = row.original.sales_coordinator?.id;
        const filters = createCompanyFilters(salesCoordinatorId, "billed", {
          due_date: {
            _gte: formatDateForAPI(subDays(new Date(), 30)),
            _lte: formatDateForAPI(subDays(new Date(), 15)),
          },
        });

        return (
          <Link
            className="hover:underline"
            to={createFilteredUrl("../status/billed", filters)}
            target="_blank"
            rel="noopener noreferrer"
          >
            <DisplayAmount value={getValue()} />
          </Link>
        );
      },
      footer: (totals) => {
        return <DisplayAmount value={totals.sixteenToThirty} />;
      },
    },
    {
      accessorKey: "thirtyToSixty",
      header: "30-60 Days",
      cell: ({ getValue, row }) => {
        const salesCoordinatorId = row.original.sales_coordinator?.id;
        const filters = createCompanyFilters(salesCoordinatorId, "billed", {
          due_date: {
            _gte: formatDateForAPI(subDays(new Date(), 60)),
            _lte: formatDateForAPI(subDays(new Date(), 30)),
          },
        });

        return (
          <Link
            className="hover:underline"
            to={createFilteredUrl("../status/billed", filters)}
            target="_blank"
            rel="noopener noreferrer"
          >
            <DisplayAmount value={getValue()} />
          </Link>
        );
      },
      footer: (totals) => {
        return <DisplayAmount value={totals.thirtyToSixty} />;
      },
    },
    {
      accessorKey: "sixtyToNinety",
      header: "60-90 Days",
      cell: ({ getValue, row }) => {
        const salesCoordinatorId = row.original.sales_coordinator?.id;
        const filters = createCompanyFilters(salesCoordinatorId, "billed", {
          due_date: {
            _gte: formatDateForAPI(subDays(new Date(), 90)),
            _lte: formatDateForAPI(subDays(new Date(), 60)),
          },
        });

        return (
          <Link
            className="hover:underline"
            to={createFilteredUrl("../status/billed", filters)}
            target="_blank"
            rel="noopener noreferrer"
          >
            <DisplayAmount value={getValue()} />
          </Link>
        );
      },
      footer: (totals) => {
        return <DisplayAmount value={totals.sixtyToNinety} />;
      },
    },
    {
      accessorKey: "greaterThanNinety",
      header: "90+ Days",
      cell: ({ getValue, row }) => {
        const salesCoordinatorId = row.original.sales_coordinator?.id;
        const filters = createCompanyFilters(salesCoordinatorId, "billed", {
          due_date: {
            _lte: formatDateForAPI(subDays(new Date(), 90)),
            _gte: formatDateForAPI(FIRST_DUE_DATE),
          },
        });

        return (
          <Link
            className="hover:underline"
            to={createFilteredUrl("../status/billed", filters)}
            target="_blank"
            rel="noopener noreferrer"
          >
            <DisplayAmount value={getValue()} />
          </Link>
        );
      },
      footer: (totals) => {
        return <DisplayAmount value={totals.greaterThanNinety} />;
      },
    },
  ];

  return (
    <CustomList
      title="Sales coordinator's billables"
      endPoint="custom-ui/get-sc-wise-billables"
      columns={columns}
      hidePagination={true}
    />
  );
}
